import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { DialogsState, Dialog, Message } from '@/types';
import { updateLead } from '@/store/leads';
import { request } from './helper';

// **Thunks**
export const dialogsGet = createAsyncThunk<Dialog[]>('dialogs/get', async () => {
  const response = await request('api/dialog');
  return response.success ? response.data : [];
});

export const dialogsGetConversation = createAsyncThunk<
  Message[],
  number
>('dialogs/getConversation', async (dialogId) => {
  const response = await request(`api/dialog/${dialogId}/message`);
  return response.success ? response.data : [];
});

export const dialogsSendMessage = createAsyncThunk<
  boolean,
  { id: number; data: any }
>('dialogs/sendMessage', async ({ id, data }: { id: number; data: any }, { dispatch, rejectWithValue }) => {
  const response = await request(`api/dialog/message/${id}`, 'POST', data);
  const updatedLead = response.data;
  dispatch(updateLead(updatedLead));
  return response.success;
});

export const dialogSendGLSMessage = createAsyncThunk<
  boolean,
  { id: number; data: any }
>('dialogs/sendMessage', async ({ id, data }: { id: number; data: any }, { dispatch, rejectWithValue }) => {
  const response = await request(`api/dialog/gls-message/${id}`, 'POST', data);
  const updatedLead = response.data;
  dispatch(updateLead(updatedLead));
  return response.success;
});

// **Initial State**
const initialState: DialogsState = {
  dialogs: [],
  currentMessages: {},
  activeDialog: null,
  status: 'idle',
};

// **Slice**
const dialogsSlice = createSlice({
  name: 'dialogs',
  initialState,
  reducers: {
    setActiveDialog: (state, action: PayloadAction<Dialog | null>) => {
      state.activeDialog = action.payload;
    },
  },
  extraReducers: (builder) => {
    const setLoading = (state: DialogsState) => {
      state.status = 'loading';
    };
    const setFailed = (state: DialogsState) => {
      state.status = 'failed';
    };

    // Get Dialogs
    builder
      .addCase(dialogsGet.pending, setLoading)
      .addCase(dialogsGet.fulfilled, (state, { payload }) => {
        state.status = 'success';
        state.dialogs = payload;
      })
      .addCase(dialogsGet.rejected, setFailed);

    // Get Conversation Messages
    builder
      .addCase(dialogsGetConversation.pending, setLoading)
      .addCase(dialogsGetConversation.fulfilled, (state, { payload, meta }) => {
        state.status = 'success';
        state.currentMessages[meta.arg] = payload; // Use dialog ID as key
      })
      .addCase(dialogsGetConversation.rejected, setFailed);

    // Send Message
    builder
      .addCase(dialogsSendMessage.pending, setLoading)
      .addCase(dialogsSendMessage.fulfilled, (state) => {
        state.status = 'success';
      })
      .addCase(dialogsSendMessage.rejected, setFailed);
  },
});

// **Export Actions**
export const { setActiveDialog } = dialogsSlice.actions;

// **Export Reducer**
export default dialogsSlice.reducer;